import api from '@api';
import Chip from '@components/common/Chip';
import AudienceOverlap from '@components/layout/main/AudienceOverlap';
import InsightChartsBlock from '@components/layout/main/insightCharts/InsightChartsBlock';
import config from '@config';
import type { AudienceType } from '@enums';
import DataQuestionIcon from '@icons/data-question.svg?react';
import { useChatStore } from '@store/chat';
import { useShellStore } from '@store/shell';
import { Tooltip } from '@ui/tooltip';
import {
  flattenAgentLinkChildren,
  getAudienceMetadata,
  isInlineChart,
  log,
  mapUrlToInsightChartsBlockProps,
  parseAudienceOverlapIds
} from '@utils';
import type { MouseEvent, ReactNode } from 'react';
import { Fragment } from 'react';
import type { Insight } from 'types/schema';
import Styled from './AgentReference.styled';

function AgentReference({
  children,
  href,
  ...props
}: {
  children: ReactNode;
  href?: string;
  [key: string]: unknown;
}) {
  const chatId = useChatStore((store) => store.chat.id);
  const setInsightList = useChatStore((store) => store.setInsightList);
  const setRightPanelProps = useShellStore((store) => store.setRightPanelProps);
  const setIsRightPanelOpen = useShellStore(
    (store) => store.setIsRightPanelOpen
  );
  const setIsSidebarExpanded = useShellStore(
    (store) => store.setIsSidebarExpanded
  );
  let url: URL;

  try {
    url = new URL(href);
  } catch {
    // Render links with malformed URLs as plain text
    return <span {...props}>{children}</span>;
  }

  if (url.protocol !== 'spark:') {
    return (
      <a href={href} {...props}>
        {children}
      </a>
    );
  }

  if (url.hostname === 'audience-overlap') {
    const ids = parseAudienceOverlapIds(url.toString());

    if (ids.length === 0) {
      return null;
    }

    log(`Rendering audience overlap: ${ids.join(', ')}`);

    return (
      <Styled.InlineChart>
        <AudienceOverlap audienceIds={ids} />
      </Styled.InlineChart>
    );
  }

  if (url.hostname === 'question-panel') {
    // Panel opens via parseQuestionPanelUrl in Main;
    return <span>{children}</span>;
  }

  const modifiedProps: Record<string, unknown> = {
    ...props,
    href: undefined,
    className: 'agent-link',
    title: undefined
  };
  let content: ReactNode = children;

  if (url.hostname === 'attrs' || url.hostname === 'tooltip') {
    content = (
      <Tooltip
        content={content}
        enterDelay={200}
        enterTouchDelay={200}
        leaveDelay={0}
        variant="white">
        <Styled.Anchor className="agent-link-anchor">
          <DataQuestionIcon className="icon" />
        </Styled.Anchor>
      </Tooltip>
    );
  }

  if (url.hostname === 'audience') {
    log(`Rendering audience chip: ${url.toString()}`);
    const audienceType = url.searchParams.get('type') as AudienceType;
    const { icon, description } = getAudienceMetadata(audienceType);

    modifiedProps.title = description;
    modifiedProps.className = 'audience';
    content = <Chip icon={icon}>{content}</Chip>;
  }

  if (url.hostname === 'sources') {
    const total = url.searchParams.get('total');
    const linkLabel = flattenAgentLinkChildren(content).trim();
    const insightIds = url.searchParams.get('insights');

    if (isInlineChart(url, linkLabel)) {
      return (
        <Styled.InlineChart>
          <InsightChartsBlock
            {...mapUrlToInsightChartsBlockProps(url, linkLabel)}
          />
        </Styled.InlineChart>
      );
    }

    modifiedProps.onClick = (event: MouseEvent) => {
      event.preventDefault();
      const target = event.currentTarget;

      if (target.classList.contains('active')) {
        setIsRightPanelOpen(false);
        return;
      }

      // Remove the active class from the previous active link
      document.querySelector('.agent-link.active')?.classList.remove('active');

      // Make the link appear as active
      target.classList.add('active');

      // Log the opening of the sources
      log(`Opening sources: ${url.toString()}`);

      // Get the text content of the link
      const textElement = target.querySelector('.agent-link-text');

      // Auto-collapse the sidebar if the window is less than 1920px
      if (window.innerWidth < config.breakpoints.xxLarge) {
        setIsSidebarExpanded(false);
      }

      // Open the right panel
      setRightPanelProps({
        content: textElement?.textContent,
        isOpen: true,
        onToggleCallback: ((element: HTMLElement) => {
          element.classList.toggle('active');
        }).bind(this, target)
      });

      if (insightIds) {
        const insights: Insight[] = insightIds.split(',').map((id) => ({ id }));

        setInsightList(insights);

        // Track event
        api.analytics.openedSources({
          ['chat_id']: chatId,
          ['nb_sources']: insights.length
        });
      }
    };

    content = (
      <Fragment>
        {content && <span className="agent-link-text">{content}</span>}
        <Styled.Anchor data-testid="agent-link-anchor">
          {total && <span>{total}</span>}
          <DataQuestionIcon className="icon" />
        </Styled.Anchor>
      </Fragment>
    );
  }

  return <Styled.Reference {...modifiedProps}>{content}</Styled.Reference>;
}

export default AgentReference;
